import React, { memo } from 'react';
import { ClipboardCheckIcon, BookOpenIcon, TrophyIcon, CheckIcon, LockIcon, ShieldIcon, WifiIcon, SmartphoneIcon, CreditCardIcon, EyeOffIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { TopBar } from '../components/TopBar';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
export function HowItWorksPage() {
  return <div className="flex flex-col min-h-screen w-full" data-id="element-1839">
      <TopBar data-id="element-1840" />
      <Header data-id="element-1841" />

      {/* Hero Section */}
      <section className="w-full bg-white py-20 md:py-28 border-b border-gray-200" data-id="element-1842">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-1843">
          <div className="max-w-4xl mx-auto text-center" data-id="element-1844">
            <h1 className="mb-6 text-5xl md:text-6xl font-extrabold text-[#111111] leading-tight" data-id="element-1845">
              From "I know we should do something" to actually done.
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 leading-relaxed" data-id="element-1846">
              Most families never get around to the basics — not because they
              don't care, but because nothing makes it easy. Caveo walks every
              person through the actions that matter, checks that they're done,
              and keeps everyone on track.
            </p>
          </div>
        </div>
      </section>

      {/* The 3-Step Journey */}
      <section className="w-full bg-[#f8f8f8] py-16 md:py-24" data-id="element-1847">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-1848">
          <div className="text-center mb-16" data-id="element-1849">
            <h2 className="text-3xl md:text-4xl font-bold text-[#111111] mb-4" data-id="element-1850">
              Here's how Caveo gets your family protected — and keeps them that
              way.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12" data-id="element-1851">
            <div className="bg-white rounded-2xl p-8 border-2 border-gray-200 text-center relative" data-id="element-1852">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-[#631bb7] text-white w-10 h-10 rounded-full flex items-center justify-center font-bold" data-id="element-1853">
                1
              </div>
              <div className="w-20 h-20 bg-[#631bb7] bg-opacity-5 rounded-2xl flex items-center justify-center mx-auto mb-6 border border-[#631bb7] border-opacity-10" data-id="element-1854">
                <ClipboardCheckIcon className="w-10 h-10 text-[#631bb7]" data-id="element-1855" />
              </div>
              <h3 className="text-2xl font-bold text-[#111111] mb-4" data-id="element-1856">
                See What's Been Missed
              </h3>
              <p className="text-gray-600 leading-relaxed mb-4" data-id="element-1857">
                A few plain-English questions about your household reveals
                exactly what hasn't been done and what matters most. No
                technical knowledge needed to answer them.
              </p>
              <ul className="text-left space-y-2" data-id="element-1858">
                <li className="flex items-center text-sm text-gray-500" data-id="element-1859">
                  <CheckIcon className="w-4 h-4 text-[#631bb7] mr-2" data-id="element-1860" />
                  Takes 3 minutes. No jargon. No scoring system to decode.
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-2xl p-8 border-2 border-gray-200 text-center relative" data-id="element-1861">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-[#631bb7] text-white w-10 h-10 rounded-full flex items-center justify-center font-bold" data-id="element-1862">
                2
              </div>
              <div className="w-20 h-20 bg-[#631bb7] bg-opacity-5 rounded-2xl flex items-center justify-center mx-auto mb-6 border border-[#631bb7] border-opacity-10" data-id="element-1863">
                <BookOpenIcon className="w-10 h-10 text-[#631bb7]" data-id="element-1864" />
              </div>
              <h3 className="text-2xl font-bold text-[#111111] mb-4" data-id="element-1865">
                Do What Matters
              </h3>
              <p className="text-gray-600 leading-relaxed mb-4" data-id="element-1866">
                Caveo walks each family member through the actions that actually
                protect them — step by step, in plain English, until they're
                done. Not just informed. Done.
              </p>
              <ul className="text-left space-y-2" data-id="element-1867">
                <li className="flex items-center text-sm text-gray-500" data-id="element-1868">
                  <CheckIcon className="w-4 h-4 text-[#631bb7] mr-2" data-id="element-1869" />
                  Every guide is built for people who aren't technical — and
                  most take under 15 minutes.
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-2xl p-8 border-2 border-gray-200 text-center relative" data-id="element-1870">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-[#631bb7] text-white w-10 h-10 rounded-full flex items-center justify-center font-bold" data-id="element-1871">
                3
              </div>
              <div className="w-20 h-20 bg-[#631bb7] bg-opacity-5 rounded-2xl flex items-center justify-center mx-auto mb-6 border border-[#631bb7] border-opacity-10" data-id="element-1872">
                <TrophyIcon className="w-10 h-10 text-[#631bb7]" data-id="element-1873" />
              </div>
              <h3 className="text-2xl font-bold text-[#111111] mb-4" data-id="element-1874">
                Stay Protected
              </h3>
              <p className="text-gray-600 leading-relaxed mb-4" data-id="element-1875">
                Caveo doesn't stop when the setup is done. Ongoing check-ins and
                coordinated reminders make sure safe habits stick across your
                whole household — not just yours.
              </p>
              <ul className="text-left space-y-2" data-id="element-1876">
                <li className="flex items-center text-sm text-gray-500" data-id="element-1877">
                  <CheckIcon className="w-4 h-4 text-[#631bb7] mr-2" data-id="element-1878" />
                  One family dashboard keeps everyone accountable — without you
                  having to manage it yourself.
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* The 3-Level Education System */}
      <section className="w-full bg-white py-16 md:py-24 border-t border-gray-200" data-id="element-1879">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-1880">
          <div className="text-center mb-16" data-id="element-1881">
            <h2 className="text-3xl md:text-4xl font-bold text-[#111111] mb-4" data-id="element-1882">
              39 guided actions. Every critical thing your family hasn't done
              yet.
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto" data-id="element-1883">
              Caveo covers what matters most — from password protection to
              financial account security to knowing what to do when something
              goes wrong. Every guide is a step-by-step walkthrough, not a
              lesson to sit through.
            </p>
          </div>

          {/* Level 1 */}
          <div className="mb-12" data-id="element-1884">
            <div className="flex items-center mb-6" data-id="element-1885">
              <div className="bg-green-100 text-green-700 px-4 py-2 rounded-lg font-bold text-sm uppercase tracking-wide mr-4" data-id="element-1886">
                Start Here — The actions that protect most people most of the
                time
              </div>
            </div>
            <p className="text-gray-600 mb-6" data-id="element-1887">
              The fundamentals everyone needs. Perfect for beginners or anyone
              wanting to solidify their basics.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-1888">
              {[{
              icon: LockIcon,
              title: 'Creating Bulletproof Passwords',
              desc: 'Learn to create and manage strong passwords without memorizing them all'
            }, {
              icon: ShieldIcon,
              title: 'Recognizing Scams & Phishing',
              desc: 'Spot fake emails, texts, and calls before they trick you'
            }, {
              icon: WifiIcon,
              title: 'Securing Your Home WiFi',
              desc: 'Protect your network from unwanted visitors'
            }].map((module, index) => <div key={index} className="bg-[#f8f8f8] rounded-xl p-5 border-2 border-gray-200" data-id="element-1889">
                  <module.icon className="w-6 h-6 text-green-600 mb-3" data-id="element-1890" />
                  <h4 className="font-bold text-[#111111] mb-2" data-id="element-1891">
                    {module.title}
                  </h4>
                  <p className="text-sm text-gray-600" data-id="element-1892">{module.desc}</p>
                </div>)}
            </div>
          </div>

          {/* Level 2 */}
          <div className="mb-12" data-id="element-1893">
            <div className="flex items-center mb-6" data-id="element-1894">
              <div className="bg-blue-100 text-blue-700 px-4 py-2 rounded-lg font-bold text-sm uppercase tracking-wide mr-4" data-id="element-1895">
                Go Deeper — For the things most families overlook
              </div>
            </div>
            <p className="text-gray-600 mb-6" data-id="element-1896">
              Build on your foundation with more advanced protection strategies.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-1897">
              {[{
              icon: CreditCardIcon,
              title: 'Protecting Financial Accounts',
              desc: 'Secure your bank accounts, credit cards, and investment apps'
            }, {
              icon: SmartphoneIcon,
              title: 'Mobile Device Security',
              desc: 'Keep your phone and tablet safe from threats'
            }, {
              icon: EyeOffIcon,
              title: 'Privacy Settings Deep Dive',
              desc: 'Take control of what apps and websites know about you'
            }].map((module, index) => <div key={index} className="bg-[#f8f8f8] rounded-xl p-5 border-2 border-gray-200" data-id="element-1898">
                  <module.icon className="w-6 h-6 text-blue-600 mb-3" data-id="element-1899" />
                  <h4 className="font-bold text-[#111111] mb-2" data-id="element-1900">
                    {module.title}
                  </h4>
                  <p className="text-sm text-gray-600" data-id="element-1901">{module.desc}</p>
                </div>)}
            </div>
          </div>

          {/* Level 3 */}
          <div data-id="element-1902">
            <div className="flex items-center mb-6" data-id="element-1903">
              <div className="bg-[#631bb7] bg-opacity-20 text-[#631bb7] px-4 py-2 rounded-lg font-bold text-sm uppercase tracking-wide mr-4" data-id="element-1904">
                For the Thorough Ones — Complete coverage, including the things
                you haven't thought of yet
              </div>
            </div>
            <p className="text-gray-600 mb-6" data-id="element-1905">
              Expert-level topics for those who want comprehensive protection.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-id="element-1906">
              {['AI Safety Primer', 'Digital Legacy Planning', 'Identity Masking', 'Cloud Storage Security', 'Smart Home Protection', 'Travel Security', 'Social Media Privacy', 'Data Broker Removal'].map((topic, index) => <div key={index} className="bg-[#f8f8f8] rounded-lg p-4 border border-gray-200 text-center" data-id="element-1907">
                  <span className="text-sm font-medium text-[#111111]" data-id="element-1908">
                    {topic}
                  </span>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* Sample Module Preview */}
      <section className="w-full bg-[#f8f8f8] py-16 md:py-24 border-t border-gray-200" data-id="element-1909">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-1910">
          <div className="text-center mb-12" data-id="element-1911">
            <h2 className="text-3xl md:text-4xl font-bold text-[#111111] mb-4" data-id="element-1912">
              Here's what a Caveo walkthrough looks like.
            </h2>
          </div>

          <div className="max-w-4xl mx-auto bg-white rounded-2xl border-2 border-gray-200 overflow-hidden" data-id="element-1913">
            {/* Module Header */}
            <div className="bg-[#1a0647] p-6" data-id="element-1914">
              <div className="flex items-center justify-between" data-id="element-1915">
                <div data-id="element-1916">
                  <span className="text-[#00f4f8] text-sm font-semibold" data-id="element-1917">
                    Start Here
                  </span>
                  <h3 className="text-white text-xl font-bold mt-1" data-id="element-1918">
                    Recognizing Scams & Phishing
                  </h3>
                </div>
                <div className="text-white text-right" data-id="element-1919">
                  <span className="text-sm opacity-70" data-id="element-1920">Progress</span>
                  <div className="text-lg font-bold" data-id="element-1921">3 of 8 steps</div>
                </div>
              </div>
              <div className="mt-4 bg-white bg-opacity-20 rounded-full h-2" data-id="element-1922">
                <div className="bg-[#00f4f8] h-2 rounded-full" style={{
                width: '37.5%'
              }} data-id="element-1923"></div>
              </div>
            </div>

            {/* Module Content */}
            <div className="p-8" data-id="element-1924">
              <h4 className="text-2xl font-bold text-[#111111] mb-4" data-id="element-1925">
                Step 3: Check the sender's email address
              </h4>
              <p className="text-gray-600 mb-6 leading-relaxed" data-id="element-1926">
                Scammers often use email addresses that look similar to real
                companies but have small differences. Here's how to spot them:
              </p>

              {/* Visual Example */}
              <div className="bg-[#f8f8f8] rounded-xl p-6 mb-6 border-2 border-gray-200" data-id="element-1927">
                <p className="text-sm font-semibold text-gray-500 mb-4" data-id="element-1928">
                  EXAMPLE: Spot the fake
                </p>
                <div className="space-y-3" data-id="element-1929">
                  <div className="flex items-center bg-white rounded-lg p-4 border-2 border-green-200" data-id="element-1930">
                    <CheckIcon className="w-5 h-5 text-green-500 mr-3" data-id="element-1931" />
                    <span className="font-mono text-sm" data-id="element-1932">
                      support@amazon.com
                    </span>
                    <span className="ml-auto text-green-600 text-sm font-medium" data-id="element-1933">
                      Real
                    </span>
                  </div>
                  <div className="flex items-center bg-white rounded-lg p-4 border-2 border-red-200" data-id="element-1934">
                    <span className="text-red-500 mr-3" data-id="element-1935">✗</span>
                    <span className="font-mono text-sm" data-id="element-1936">
                      support@amaz0n-secure.com
                    </span>
                    <span className="ml-auto text-red-600 text-sm font-medium" data-id="element-1937">
                      Fake
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between" data-id="element-1938">
                <button className="text-gray-500 font-medium hover:text-[#631bb7] transition-colors" data-id="element-1939">
                  ← Previous Step
                </button>
                <button className="bg-[#631bb7] text-white px-6 py-3 rounded-lg font-bold hover:bg-[#521099] transition-colors" data-id="element-1940">
                  Next Step →
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="w-full bg-white py-16 md:py-24 border-t border-gray-200" data-id="element-1941">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-1942">
          <div className="max-w-3xl mx-auto text-center" data-id="element-1943">
            <h2 className="text-3xl md:text-4xl font-bold text-[#111111] mb-4" data-id="element-1944">
              Your family's gaps are 3 minutes away from being visible.
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-8" data-id="element-1945">
              See what's been missed — and get a clear plan for fixing it. Free,
              no email required.
            </p>
            <Link to="/onboarding" className="bg-[#631bb7] text-white px-8 py-4 rounded-lg font-bold inline-flex items-center justify-center no-underline hover:bg-[#521099] transition-all shadow-md" data-id="element-1946">
              Start My Assessment
            </Link>
          </div>
        </div>
      </section>

      <Footer data-id="element-1947" />
    </div>;
}